<div class="wizard-shell">
    <div class="wizard-card">
        <div class="wizard-header">
            <h2 class="mb-1">VanCon Inc.</h2>
            <p class="mb-0">Subcontractor Safety Training</p>
        </div>

        <div class="wizard-body">
            @if (step === 'loading') {
                <div class="text-center py-5">
                    <div class="spinner-border text-primary mb-3" role="status"></div>
                    <p class="text-muted mb-0">Loading your training...</p>
                </div>
            }

            @if (step === 'error') {
                <div class="text-center py-5">
                    <h4 class="mb-3">Unable to Open Training</h4>
                    <p>{{ errorMessage }}</p>
                </div>
            }

            @if (step === 'resume-pick' && invite) {
                <h4 class="mb-3">Welcome Back</h4>
                <p><strong>Company:</strong> {{ invite.companyName }}</p>
                <p class="text-muted">We found training already in progress for this link. Select your name to pick up where you left off.</p>

                <div class="list-group mb-3">
                    @for (participant of resumableParticipants; track participant.participantId) {
                        <button
                            type="button"
                            class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                            (click)="resumeAs(participant.participantId)"
                        >
                            <span>{{ participant.firstName }} {{ participant.lastName }}</span>
                            <span class="badge bg-secondary">{{ participant.completedModuleCount }} of {{ participant.totalModuleCount }} modules complete</span>
                        </button>
                    }
                </div>

                <button type="button" class="btn btn-outline-secondary" (click)="startAsNewPerson()">
                    I'm someone else / Start new training
                </button>
            }

            @if (step !== 'loading' && step !== 'error' && step !== 'resume-pick' && invite) {
                <div class="wizard-steps">
                    <div class="wizard-step" [class.active]="step === 'welcome'" [class.completed]="step !== 'welcome'">
                        <div class="wizard-step-circle">1</div>
                        <div class="wizard-step-label">Welcome</div>
                    </div>
                    <div class="wizard-step" [class.active]="step === 'video'" [class.completed]="step === 'quiz' || step === 'complete'">
                        <div class="wizard-step-circle">2</div>
                        <div class="wizard-step-label">Video</div>
                    </div>
                    <div class="wizard-step" [class.active]="step === 'quiz'" [class.completed]="step === 'complete'">
                        <div class="wizard-step-circle">3</div>
                        <div class="wizard-step-label">Quiz</div>
                    </div>
                </div>

                @if (step === 'welcome') {
                    <p>{{ invite.welcomeText }}</p>
                    <p><strong>Company:</strong> {{ invite.companyName }}</p>
                    <p class="text-muted">Enter your name to begin training. If you were forwarded this link, each employee should complete training separately.</p>

                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group mb-3">
                                <label>First Name</label>
                                <input type="text" class="form-control" [(ngModel)]="firstName" name="firstName" />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group mb-3">
                                <label>Last Name</label>
                                <input type="text" class="form-control" [(ngModel)]="lastName" name="lastName" />
                            </div>
                        </div>
                    </div>

                    <button type="button" class="btn btn-primary" [disabled]="!canProceedFromWelcome" (click)="continueFromWelcome()">
                        Next
                    </button>
                }

                @if (step === 'video' && currentModule) {
                    <p class="text-muted mb-2">{{ moduleProgressLabel }}</p>
                    <h4 class="mb-3">{{ currentModule.title }}</h4>
                    <p>{{ currentModule.description }}</p>

                    @if (currentYouTubeVideoId) {
                        <div class="video-container mb-3">
                            <div #youtubePlayerHost class="youtube-player-host"></div>
                        </div>
                        <p class="small text-muted mb-3">
                            @if (videoWatchRequirementMet) {
                                You have watched enough of this video to continue.
                            } @else {
                                Watch at least 92% of the video without skipping ahead to continue. Progress:
                                {{ videoWatchProgressPercent }}%
                            }
                        </p>
                    } @else {
                        <div class="alert alert-warning">
                            The training video for this module is not yet configured. Please contact your VanCon representative.
                        </div>
                    }

                    <div class="form-check mb-3">
                        <input
                            id="videoAcknowledged"
                            type="checkbox"
                            class="form-check-input"
                            [(ngModel)]="videoAcknowledged"
                            name="videoAcknowledged"
                            [disabled]="!canAcknowledgeVideo"
                        />
                        <label class="form-check-label" for="videoAcknowledged">I have watched the video</label>
                    </div>

                    <button type="button" class="btn btn-primary" [disabled]="!canProceedFromVideo" (click)="continueFromVideo()">
                        Next
                    </button>
                }

                @if (step === 'quiz' && currentModule) {
                    <p class="text-muted mb-2">{{ moduleProgressLabel }}</p>
                    <h4 class="mb-3">{{ currentModule.title }} Quiz</h4>

                    @for (question of currentModule.questions; track question.id) {
                        <div class="quiz-question">
                            <div class="fw-semibold mb-3">{{ question.sortOrder }}. {{ question.questionText }}</div>

                            @if (question.questionType === 'TrueFalse') {
                                <label class="quiz-option">
                                    <input
                                        type="radio"
                                        [name]="'question-' + question.id"
                                        value="True"
                                        [checked]="questionStates[question.id]?.selectedAnswer === 'True'"
                                        (change)="selectAnswer(question.id, 'True')"
                                    />
                                    True
                                </label>
                                <label class="quiz-option">
                                    <input
                                        type="radio"
                                        [name]="'question-' + question.id"
                                        value="False"
                                        [checked]="questionStates[question.id]?.selectedAnswer === 'False'"
                                        (change)="selectAnswer(question.id, 'False')"
                                    />
                                    False
                                </label>
                            } @else {
                                @for (option of question.options; track option) {
                                    <label class="quiz-option">
                                        <input
                                            type="radio"
                                            [name]="'question-' + question.id"
                                            [value]="option"
                                            [checked]="questionStates[question.id]?.selectedAnswer === option"
                                            (change)="selectAnswer(question.id, option)"
                                        />
                                        {{ option }}
                                    </label>
                                }
                            }

                            @if (questionStates[question.id]?.isCorrect === true) {
                                <div class="feedback-box correct">Correct.</div>
                            }
                            @if (questionStates[question.id]?.isCorrect === false) {
                                <div class="feedback-box">
                                    <div><strong>Correct answer:</strong> {{ questionStates[question.id].correctAnswer }}</div>
                                    @if (questionStates[question.id].explanation) {
                                        <div class="mt-1">{{ questionStates[question.id].explanation }}</div>
                                    }
                                </div>
                            }
                        </div>
                    }

                    @if (submitMessage) {
                        <div class="alert alert-warning">{{ submitMessage }}</div>
                    }

                    <button
                        type="button"
                        class="btn btn-primary"
                        [disabled]="!allQuestionsCorrect || isSubmitting"
                        (click)="submitQuiz()"
                    >
                        @if (isSubmitting) {
                            Submitting...
                        } @else {
                            {{ submitQuizButtonLabel }}
                        }
                    </button>
                }

                @if (step === 'complete') {
                    <div class="text-center py-4">
                        <h4 class="mb-3">Training Complete</h4>
                        <p>Thank you for completing your subcontractor safety training.</p>
                        <p class="text-muted">You may close this page, or register another employee below.</p>
                        <button type="button" class="btn btn-outline-primary mt-3" (click)="registerAnotherEmployee()">
                            Another employee needs to complete training
                        </button>
                    </div>
                }
            }
        </div>
    </div>
</div>
